import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useUserStore } from '../stores/useUserStore';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Save, Tag, FileText, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import CostService from '../services/costService';
import { nekoPos } from 'public-contracts';
import { useToast } from '../hooks/useToast';

type CostCategory = nekoPos.CostCategory;

interface CostCategoryFormData {
  name: string;
  description: string;
  parent: string;
  isDefault: boolean;
}

const ModernCostCategoryCreate: React.FC = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const { user, fetchUser } = useUserStore();
  const costService = useMemo(() => new CostService(), []);
  const { success, error: showError } = useToast();

  const [saving, setSaving] = useState(false);
  const [parentCategories, setParentCategories] = useState<CostCategory[]>([]);

  // Ensure user data is loaded
  useEffect(() => {
    if (!user) {
      fetchUser();
    }
  }, [user, fetchUser]);

  // Load parent categories for dropdown
  useEffect(() => {
    const loadParentCategories = async () => {
      try {
        const categoriesData = await costService.getCostCategories({
          perPage: 1000, // Get all categories for the dropdown
        });
        const categoriesArray = Array.isArray(categoriesData)
          ? categoriesData
          : [];
        setParentCategories(categoriesArray);
      } catch (error) {
        console.error('Error loading parent categories:', error);
        setParentCategories([]);
      }
    };
    loadParentCategories();
  }, [costService]);

  const methods = useForm<CostCategoryFormData>({
    defaultValues: {
      name: '',
      description: '',
      parent: '',
      isDefault: false,
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const hasPermission = (action: 'list' | 'create' | 'edit' | 'delete') => {
    const roles = user?.roles || [];
    console.log('CostCategoryCreate - Action:', action);
    console.log('CostCategoryCreate - User:', user?.fullName);
    console.log('CostCategoryCreate - Roles:', roles);
    console.log('CostCategoryCreate - Roles string:', JSON.stringify(roles));

    // If user is not loaded yet, allow basic operations
    if (!user) {
      console.log('CostCategoryCreate - No user, allowing basic operations');
      return ['list', 'create', 'edit'].includes(action);
    }
    if (user.roles?.includes('sysAdmin')) {
      console.log('CostCategoryCreate - User is sysAdmin, allowing');
      return true;
    }
    if (user.roles?.includes('admin')) {
      console.log('CostCategoryCreate - User is admin, allowing');
      return true;
    }
    if (user.roles?.includes('staff')) {
      console.log('CostCategoryCreate - User is staff, allowing');
      return true;
    }
    if (user.roles?.includes('storeManager')) {
      console.log('CostCategoryCreate - User is storeManager, allowing');
      return true;
    }
    if (user.roles?.includes('cashier')) {
      console.log('CostCategoryCreate - User is cashier, checking action');
      return action !== 'delete';
    }

    // If user has no roles or empty roles, allow basic operations
    if (!user.roles || user.roles.length === 0) {
      console.log(
        'CostCategoryCreate - User has no roles, allowing basic operations'
      );
      return ['list', 'create', 'edit'].includes(action);
    }

    console.log('CostCategoryCreate - Permission denied');
    return false;
  };

  if (!hasPermission('create')) {
    return (
      <div className="flex items-center justify-center h-64 bg-muted">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            {translate('pos.common.accessDenied')}
          </p>
          <p className="text-muted-foreground">
            {translate('pos.cost_categories.no_permission_create')}
          </p>
        </div>
      </div>
    );
  }

  const onSubmit = async (data: CostCategoryFormData) => {
    setSaving(true);
    try {
      console.log('🚀 Starting cost category creation...');
      console.log('📝 Form data:', data);

      const categoryData: Omit<CostCategory, 'id'> = {
        name: data.name,
        description: data.description,
        parent: data.parent || '',
        isDefault: data.isDefault,
      };

      console.log('📦 Category data to send:', categoryData);
      console.log('👤 User:', user?.fullName, 'Roles:', user?.roles);

      const result = await costService.createCostCategory(categoryData);
      console.log('✅ Cost category created successfully:', result);
      success(
        translate(
          'pos.cost_categories.categoryCreated',
          'Cost category created successfully'
        )
      );
      navigate('/cost_categories');
    } catch (error) {
      console.error('❌ Error creating cost category:', error);
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        error: error,
      });

      const errorMessage =
        error instanceof Error
          ? error.message
          : translate('pos.cost_categories.create_error');

      showError(
        `${translate('pos.cost_categories.create_error')}: ${errorMessage}`
      );
      // Don't navigate on error - stay on the form
      return;
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/cost_categories');
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={handleCancel}
          className="border-border text-foreground hover:bg-muted"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {translate('pos.common.action.back')}
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {translate('pos.cost_categories.create_title')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {translate('pos.cost_categories.create_subtitle')}
          </p>
        </div>
      </div>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Tag className="h-5 w-5" />
                    {translate('pos.cost_categories.basic_information')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium">
                      {translate('pos.costCategories.fields.name')} *
                    </Label>
                    <Input
                      id="name"
                      placeholder={translate(
                        'pos.cost_categories.name_placeholder'
                      )}
                      {...register('name', {
                        required: translate(
                          'pos.cost_categories.name_required'
                        ),
                      })}
                      className="mt-1"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.name.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label
                      htmlFor="description"
                      className="text-sm font-medium"
                    >
                      {translate('pos.costCategories.fields.description')} *
                    </Label>
                    <Textarea
                      id="description"
                      placeholder={translate(
                        'pos.cost_categories.description_placeholder'
                      )}
                      {...register('description', {
                        required: translate(
                          'pos.cost_categories.description_required'
                        ),
                      })}
                      className="mt-1"
                      rows={4}
                    />
                    {errors.description && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.description.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="parent" className="text-sm font-medium">
                      {translate('pos.costCategories.fields.parent')}
                    </Label>
                    <Select
                      value={watch('parent') || ''}
                      onValueChange={(value) =>
                        setValue('parent', value === 'none' ? '' : value)
                      }
                    >
                      <SelectTrigger id="parent" className="mt-1">
                        <SelectValue
                          placeholder={translate(
                            'pos.cost_categories.parent_placeholder',
                            'Select parent category (optional)'
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          {translate('pos.cost_categories.none', 'None')}
                        </SelectItem>
                        {parentCategories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="isDefault"
                      {...register('isDefault')}
                      className="rounded border-border"
                    />
                    <Label htmlFor="isDefault" className="text-sm font-medium">
                      {translate('pos.cost_categories.set_as_default')}
                    </Label>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-4 space-y-4">
                {/* Summary Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {translate('pos.cost_categories.summary')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {translate('pos.costCategories.fields.name')}:
                      </span>
                      <span className="font-medium">
                        {watch('name') ||
                          translate('pos.cost_categories.not_entered')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {translate('pos.costCategories.fields.parent')}:
                      </span>
                      <span className="font-medium">
                        {watch('parent')
                          ? parentCategories.find(
                              (cat) => cat.id === watch('parent')
                            )?.name || watch('parent')
                          : translate('pos.cost_categories.none')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {translate('pos.costCategories.fields.isDefault')}:
                      </span>
                      <span className="font-medium">
                        {watch('isDefault')
                          ? translate('pos.cost_categories.yes')
                          : translate('pos.cost_categories.no')}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Actions */}
                <Card>
                  <CardContent className="p-4 space-y-3">
                    <Button type="submit" className="w-full" disabled={saving}>
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          {translate('common.saving')}
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          {translate('pos.cost_categories.save_category')}
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancel}
                      className="w-full border-border text-foreground hover:bg-muted"
                    >
                      {translate('pos.common.action.cancel')}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default ModernCostCategoryCreate;
